/**
 * ClockInOutWidget — Story 106-3 (E-106 HR UX Max)
 *
 * Status-aware card: state color (zöld/sárga/szürke/piros), mai óra-progress,
 * heti óra-progress bar (ha van weeklyMinutes prop), forgotten-clockout warning.
 *
 * A widget NEM tart state-et a heti adathoz — a parent (AttendancePage) kalkulálja
 * a saját havi record-jaiból és prop-ban átadja. Így 106-3 scope mentén
 * NEM kell új backend endpoint (defer 106-3b).
 */

import { useTranslation } from '@kgc/i18n';
import { Button, Card, CardContent, CardHeader, CardTitle, toast } from '@kgc/ui';
import { useEffect, useMemo, useState } from 'react';
import type { AttendanceRecord } from '../api/hr-api';
import { useClockIn, useClockOut } from '../hooks/useClockInOut';

type WidgetState = 'clocked-out' | 'clocked-in' | 'overtime' | 'forgotten';

interface ClockInOutWidgetProps {
  todayRecord?: AttendanceRecord | undefined;
  /** Parent által aggregált heti munkaperc (H 00:00 → most). Opcionális — ha nincs, heti bar rejtve. */
  weeklyMinutes?: number | undefined;
  /** Heti limit percben, alapértelmezett 40h = 2400 perc */
  weeklyLimitMinutes?: number | undefined;
}

const DEFAULT_WEEKLY_LIMIT = 40 * 60; // 2400 perc
const FORGOTTEN_THRESHOLD_HOURS = 16;
const DAILY_OVERTIME_THRESHOLD_MINUTES = 8 * 60;

function parseTime(time: string | undefined): Date | null {
  if (time === undefined || time === '') return null;
  // Backend `HH:MM:SS` vagy full ISO — kompat.
  const now = new Date();
  if (time.includes('T')) {
    const d = new Date(time);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const [h, m, s] = time.split(':').map(x => parseInt(x, 10));
  if ([h, m].some(Number.isNaN)) return null;
  const d = new Date(now);
  d.setHours(h ?? 0, m ?? 0, s ?? 0, 0);
  return d;
}

function minutesBetween(start: Date, end: Date): number {
  return Math.max(0, Math.floor((end.getTime() - start.getTime()) / 60_000));
}

function formatDuration(totalMinutes: number): string {
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return `${h}h ${m}m`;
}

export function ClockInOutWidget({
  todayRecord,
  weeklyMinutes,
  weeklyLimitMinutes = DEFAULT_WEEKLY_LIMIT,
}: ClockInOutWidgetProps) {
  const { t } = useTranslation();
  const clockInMutation = useClockIn();
  const clockOutMutation = useClockOut();
  const [optimisticState, setOptimisticState] = useState<'in' | 'out' | null>(null);

  // Mai óra-progress élő frissítés: 60mp-enként re-render
  const [now, setNow] = useState<Date>(() => new Date());
  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(interval);
  }, []);

  const actualStart = useMemo(
    () => parseTime(todayRecord?.actualStart),
    [todayRecord?.actualStart]
  );
  const actualEnd = useMemo(() => parseTime(todayRecord?.actualEnd), [todayRecord?.actualEnd]);

  const isClockedIn =
    optimisticState !== null ? optimisticState === 'in' : Boolean(actualStart && !actualEnd);

  // Ma dolgozott perc
  const todayWorkedMinutes = useMemo(() => {
    if (!actualStart) return 0;
    const endRef = actualEnd ?? now;
    return minutesBetween(actualStart, endRef);
  }, [actualStart, actualEnd, now]);

  // Elfelejtett clock-out: bent + >16h óta
  const forgottenClockOut =
    isClockedIn && actualStart !== null && todayWorkedMinutes > FORGOTTEN_THRESHOLD_HOURS * 60;

  // Túlóra állapot: bent van és >8h VAGY heti >40h
  const isOvertimeToday = isClockedIn && todayWorkedMinutes > DAILY_OVERTIME_THRESHOLD_MINUTES;
  const isOvertimeWeek =
    isClockedIn && weeklyMinutes !== undefined && weeklyMinutes > weeklyLimitMinutes;

  const state: WidgetState = forgottenClockOut
    ? 'forgotten'
    : isOvertimeToday || isOvertimeWeek
      ? 'overtime'
      : isClockedIn
        ? 'clocked-in'
        : 'clocked-out';

  const stateStyles: Record<WidgetState, { dot: string; label: string }> = {
    'clocked-in': { dot: 'bg-green-500', label: t('hr.attendance.currentlyClockedIn') },
    overtime: { dot: 'bg-amber-500', label: t('hr.attendance.overtimeActive') },
    'clocked-out': { dot: 'bg-gray-400', label: t('hr.attendance.currentlyClockedOut') },
    forgotten: { dot: 'bg-red-500', label: t('hr.attendance.forgottenClockOut') },
  };

  const handleClockIn = async () => {
    try {
      setOptimisticState('in');
      await clockInMutation.mutateAsync(undefined);
      toast.success(t('hr.attendance.clockedInSuccess'));
    } catch {
      setOptimisticState(null);
      toast.error(t('hr.attendance.clockInError'));
    }
  };

  const handleClockOut = async () => {
    try {
      setOptimisticState('out');
      await clockOutMutation.mutateAsync();
      toast.success(t('hr.attendance.clockedOutSuccess'));
    } catch {
      setOptimisticState(null);
      toast.error(t('hr.attendance.clockOutError'));
    }
  };

  const isPending = clockInMutation.isPending || clockOutMutation.isPending;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('hr.attendance.title')}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4 py-6">
        {/* Forgotten clock-out banner */}
        {forgottenClockOut && (
          <div
            className="rounded-lg border border-red-500 bg-red-500/10 p-3 text-sm text-red-700 dark:text-red-300"
            role="alert"
            aria-live="polite"
          >
            <p className="font-medium">{t('hr.attendance.forgottenClockOutBanner')}</p>
            <p className="mt-1 text-xs text-muted-foreground">
              {t('hr.attendance.forgottenClockOutHint')}
            </p>
          </div>
        )}

        {/* State row */}
        <div className="flex items-center justify-center gap-2" aria-live="polite">
          <span
            className={`h-3 w-3 rounded-full transition-colors ${stateStyles[state].dot}`}
            aria-hidden="true"
          />
          <span className="text-sm text-muted-foreground">{stateStyles[state].label}</span>
        </div>

        {/* Mai óra-progress */}
        {actualStart && (
          <div className="flex flex-col items-center gap-1">
            <p className="text-xs text-muted-foreground">
              {t('hr.attendance.startedAt')}: {todayRecord?.actualStart}
              {actualEnd && ` — ${t('hr.attendance.endedAt')}: ${todayRecord?.actualEnd}`}
            </p>
            <p className="text-lg font-semibold">
              {t('hr.attendance.todayWorkedLabel')}: {formatDuration(todayWorkedMinutes)}
            </p>
          </div>
        )}

        {/* Heti óra-progress bar (csak ha parent átadta) */}
        {weeklyMinutes !== undefined && (
          <div className="w-full">
            <div className="mb-1 flex items-center justify-between text-xs text-muted-foreground">
              <span>{t('hr.attendance.weeklyProgress')}</span>
              <span
                className={
                  weeklyMinutes > weeklyLimitMinutes
                    ? 'font-semibold text-amber-600 dark:text-amber-400'
                    : ''
                }
              >
                {formatDuration(weeklyMinutes)} / {formatDuration(weeklyLimitMinutes)}
              </span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-muted">
              <div
                className={`h-full transition-all ${
                  weeklyMinutes > weeklyLimitMinutes ? 'bg-amber-500' : 'bg-primary'
                }`}
                style={{
                  width: `${Math.min(100, (weeklyMinutes / Math.max(1, weeklyLimitMinutes)) * 100)}%`,
                }}
              />
            </div>
          </div>
        )}

        {/* Fő akciógomb */}
        <div className="flex justify-center">
          {!isClockedIn ? (
            <Button
              size="lg"
              className="h-20 w-full max-w-[240px] text-xl font-bold bg-green-600 hover:bg-green-700"
              onClick={() => void handleClockIn()}
              disabled={isPending}
            >
              {t('hr.attendance.clockIn')} →
            </Button>
          ) : (
            <Button
              size="lg"
              variant="destructive"
              className="h-20 w-full max-w-[240px] text-xl font-bold"
              onClick={() => void handleClockOut()}
              disabled={isPending}
            >
              {t('hr.attendance.clockOut')} ←
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
